import { useState } from "react";
import type { InvoiceServiceType } from "../../../models/InvoiceServiceType";
import { EditIcon } from "../../atoms/icons/EditIcon";
import { theme } from "../../../theme";
import CircleButton from "../../atoms/buttons/CircleButton";
import Modal from "../Modal";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import { getInvoiceServiceTypeTranslationKey } from "../../../utils/invoicesHelpers";
import Alert from "../Alert";
import { useUpdateInvoiceServiceType } from "../../../graphql/hooks/invoices/useUpdateInvoiceServiceType";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";

interface Props {
  invoiceId: string;
  currentServiceType: InvoiceServiceType;
  buttonType: "circleOutlined" | "circleOutlinedSmall";
  disabled?: boolean;
}

const UpdateServiceTypeButton: React.FC<Props> = ({
  invoiceId,
  currentServiceType,
  buttonType,
  disabled,
}) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedServiceType, setSelectedServiceType] =
    useState<InvoiceServiceType>(currentServiceType);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onCompleted = (res?: any) => {
    if (res?.invoice?.updateInvoiceServiceType?.success) {
      setModalOpen(false);
    } else {
      setErrorMessage(t("errors.updateServiceTypeError"));
    }
  };
  const onError = (error: any) => {
    setErrorMessage(error?.message || t("errors.updateServiceTypeError"));
  };

  const { updateInvoiceServiceType, error, loading } =
    useUpdateInvoiceServiceType({
      onCompleted,
      onError,
    });

  const onClickUpdate = async () => {
    setErrorMessage(null);
    try {
      await updateInvoiceServiceType({
        variables: {
          input: {
            invoiceId,
            serviceType: selectedServiceType,
          },
        },
      });
    } catch (e) {
      setErrorMessage(e?.toString() || t("errors.updateServiceTypeError"));
    }
  };

  return (
    <div>
      {buttonType === "circleOutlined" && (
        <CircleButton
          icon={<EditIcon color={theme.colors.primary} />}
          onClick={() => setModalOpen(!modalOpen)}
          disabled={disabled}
        />
      )}
      {buttonType === "circleOutlinedSmall" && (
        <CircleButton
          icon={<EditIcon color={theme.colors.primary} />}
          onClick={() => setModalOpen(!modalOpen)}
          disabled={disabled}
          size="small"
        />
      )}

      {modalOpen && (
        <Modal
          closeIcon
          title={t("documentHandler.selectServiceType")}
          onClose={() => setModalOpen(false)}
          actionButtons={{
            actionButton: {
              buttonTitle: t("common.save"),
              loading: loading,
              onClick: onClickUpdate,
            },
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setModalOpen(false),
            },
          }}
        >
          <BodySmall>{t("documentHandler.selectServiceType")}</BodySmall>
          <HorizontalSpacer spacing={0.5} />

          <div
            style={{
              display: "flex",
              gap: 32,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RadioButtonBox
              title={t(
                getInvoiceServiceTypeTranslationKey("ADMINISTER_INVOICE"),
              )}
              small
              checked={selectedServiceType === "ADMINISTER_INVOICE"}
              onChange={() => setSelectedServiceType("ADMINISTER_INVOICE")}
            />
            <RadioButtonBox
              title={t(getInvoiceServiceTypeTranslationKey("SELL_INVOICE"))}
              small
              checked={selectedServiceType === "SELL_INVOICE"}
              onChange={() => setSelectedServiceType("SELL_INVOICE")}
            />
          </div>

          {(errorMessage || error) && (
            <Alert
              type="error"
              title={t("errors.updateServiceTypeError")}
              message={errorMessage || error?.message}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default UpdateServiceTypeButton;
